<template>
  <div class="audit-trail">
    <!-- Les filtres : sur une fiche, seulement la période et les actions système. -->
    <v-row dense align="center" class="mb-2">
      <template v-if="!compact && !entityId">
        <v-col cols="6" md="2">
          <v-select v-model="filters.module" :items="moduleItems" item-text="label" item-value="value" :label="$t('audit-trail.module')" clearable hide-details dense outlined @change="moduleChanged" />
        </v-col>
        <v-col cols="6" md="2">
          <v-autocomplete v-model="filters.entityType" :items="objectItems" item-text="label" item-value="value" :label="$t('audit-trail.object')" clearable hide-details dense outlined @change="load" />
        </v-col>
        <v-col cols="6" md="2">
          <v-select v-model="filters.channel" :items="channelItems" :label="$t('audit-trail.channel')" clearable hide-details dense outlined @change="load" />
        </v-col>
        <v-col cols="6" md="1">
          <v-select v-model="filters.outcome" :items="outcomeItems" :label="$t('audit-trail.outcome')" clearable hide-details dense outlined @change="load" />
        </v-col>
      </template>
      <v-col cols="5" :md="compact ? 5 : 2">
        <v-text-field v-model="filters.from" type="date" :label="$t('audit-trail.from')" hide-details dense outlined @change="load" />
      </v-col>
      <v-col cols="5" :md="compact ? 5 : 2">
        <v-text-field v-model="filters.to" type="date" :label="$t('audit-trail.to')" hide-details dense outlined @change="load" />
      </v-col>
      <v-col cols="2" :md="compact ? 2 : 1" class="d-flex align-center">
        <v-btn icon small :loading="exporting" :title="$t('audit-trail.export')" :aria-label="$t('audit-trail.export')" @click="exportCsv">
          <v-icon small>mdi-download</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="12" class="d-flex align-center">
        <v-switch v-model="filters.includeSystem" class="mt-0 pt-0" :label="$t('audit-trail.include-system')" hide-details dense @change="load" />
        <v-chip v-if="filters.operationId" small close class="ml-4" @click:close="showOperation(null)">{{ $t('audit-trail.one-operation') }}</v-chip>
      </v-col>
    </v-row>

    <!-- Dans le panneau : ce qui a changé se lit tout de suite, sans déplier. -->
    <template v-if="compact">
      <v-progress-linear v-if="loading" indeterminate class="mb-2" />
      <div v-if="!loading && !items.length" class="text-body-2 text--secondary text-center py-6">{{ $t('audit-trail.empty') }}</div>
      <div v-for="item in items" :key="item.id" class="audit-trail__entry">
        <div class="d-flex align-center flex-wrap">
          <v-chip x-small label :color="kindColor(item.kind)" text-color="white" class="mr-2">{{ $t(`audit-trail.kinds.${item.kind}`) }}</v-chip>
          <span class="text-body-2 font-weight-medium">{{ item.actor.label || sourceLabel(item.actor.source) }}</span>
          <span v-if="item.onBehalfOf" class="text-caption text--secondary ml-1">{{ $t('audit-trail.on-behalf-of', { name: item.onBehalfOf.label || '-' }) }}</span>
          <v-spacer />
          <span class="text-caption text--secondary text-no-wrap">{{ item.occurredAt | formatDate('DD/MM/YYYY HH:mm:ss') }}</span>
        </div>
        <div v-if="!entityId" class="text-caption">{{ item.entity.typeLabel }}<template v-if="item.entity.label"> · {{ item.entity.label }}</template></div>
        <div class="text-caption text--secondary">{{ actionLabel(item.action) }} · {{ channelLabel(item.channel) }}</div>
        <div v-if="item.reason" class="text-caption" :class="item.outcome === 'done' ? 'text--secondary' : 'error--text'">{{ reasonLabel(item.reason) }}</div>
        <table v-if="item.changes.length" class="audit-trail__fields mt-1">
          <tr v-for="change in item.changes" :key="change.field">
            <td class="text--secondary">{{ change.label }}{{ position(change) }}</td>
            <template v-if="change.secret"><td colspan="3" class="font-italic">{{ $t('audit-trail.secret') }}</td></template>
            <template v-else-if="change.masked && change.before === null && change.after === null"><td colspan="3" class="font-italic">{{ $t('audit-trail.masked') }}</td></template>
            <template v-else>
              <td class="audit-trail__before">{{ display(change.beforeLabel, change.before) }}</td>
              <td>→</td>
              <td class="audit-trail__after">{{ display(change.afterLabel, change.after) }}</td>
            </template>
          </tr>
        </table>
      </div>
    </template>

    <v-data-table
        v-else
        :headers="headers"
        :items="items"
        :loading="loading"
        :expanded.sync="expanded"
        :no-data-text="$t('audit-trail.empty')"
        item-key="id"
        show-expand
        disable-pagination
        disable-sort
        hide-default-footer
        dense
    >
      <template #item.occurredAt="{ item }">
        <span class="text-no-wrap">{{ item.occurredAt | formatDate('DD/MM/YYYY HH:mm:ss') }}</span>
      </template>
      <template #item.actor="{ item }">
        <div>{{ item.actor.label || sourceLabel(item.actor.source) }}</div>
        <div v-if="item.onBehalfOf" class="text-caption text--secondary">{{ $t('audit-trail.on-behalf-of', { name: item.onBehalfOf.label || '-' }) }}</div>
      </template>
      <template #item.action="{ item }">
        <v-chip x-small label :color="kindColor(item.kind)" text-color="white" class="mr-1">{{ $t(`audit-trail.kinds.${item.kind}`) }}</v-chip>
        <span>{{ item.entity.typeLabel }}</span>
        <span v-if="item.entity.label" class="text--secondary"> · {{ item.entity.label }}</span>
        <div class="text-caption text--secondary">{{ actionLabel(item.action) }}</div>
        <div v-if="item.reason" class="text-caption" :class="item.outcome === 'done' ? 'text--secondary' : 'error--text'">{{ reasonLabel(item.reason) }}</div>
      </template>
      <template #item.changes="{ item }">
        <span class="text-caption text--secondary">{{ summary(item) }}</span>
      </template>
      <template #item.channel="{ item }">
        <v-chip x-small label>{{ channelLabel(item.channel) }}</v-chip>
      </template>

      <!-- Le détail : ce qui a changé champ par champ et, pour l'équipe ZEborne, depuis quoi. -->
      <template #expanded-item="{ headers: columns, item }">
        <td :colspan="columns.length" class="audit-trail__detail">
          <table v-if="item.changes.length" class="audit-trail__fields mb-2">
            <tr v-for="change in item.changes" :key="change.field">
              <td class="text--secondary">{{ change.label }}{{ position(change) }}</td>
              <template v-if="change.secret"><td colspan="3" class="font-italic">{{ $t('audit-trail.secret') }}</td></template>
              <template v-else-if="change.masked && change.before === null && change.after === null"><td colspan="3" class="font-italic">{{ $t('audit-trail.masked') }}</td></template>
              <template v-else>
                <td class="audit-trail__before">{{ display(change.beforeLabel, change.before) }}</td>
                <td>→</td>
                <td class="audit-trail__after">{{ display(change.afterLabel, change.after) }}</td>
              </template>
            </tr>
          </table>
          <div v-else class="text-caption text--secondary mb-2">{{ $t('audit-trail.no-change') }}</div>
          <div class="d-flex align-center flex-wrap">
            <v-btn v-if="!entityId" x-small text color="primary" class="mr-2" @click="showOperation(item.operationId)">{{ $t('audit-trail.see-operation') }}</v-btn>
            <v-chip v-if="item.intact === false" x-small label color="error" text-color="white" class="mr-2">{{ $t('audit-trail.altered') }}</v-chip>
            <v-chip v-if="item.pseudonymized" x-small label class="mr-2">{{ $t('audit-trail.pseudonymized') }}</v-chip>
            <span v-if="item.securityTrace" class="text-caption text--secondary">{{ item.securityTrace.ip || '-' }} · {{ item.securityTrace.device || '-' }} · {{ item.securityTrace.userAgent || '-' }}</span>
            <span v-if="item.requestId" class="text-caption text--secondary ml-2">{{ $t('audit-trail.request-id') }} {{ item.requestId }}</span>
          </div>
        </td>
      </template>
    </v-data-table>

    <div v-if="next" class="text-center mt-3">
      <v-btn small outlined color="primary" :loading="loadingMore" @click="loadMore">{{ $t('audit-trail.more') }}</v-btn>
    </div>
  </div>
</template>

<script>
import AuditTrailRepository from '@repository/AuditTrailRepository'

/**
 * L'historique des actions, du plus récent au plus ancien, un événement par objet touché, dépliable (ce qui a changé
 * champ par champ). Seul composant de lecture : la page globale, et le bouton « Historique » d'un module ou d'une fiche.
 * Le périmètre est appliqué par le serveur. Pas de comptage : « voir plus » tant qu'il y en a.
 */
export default {
  name: 'AuditTrailList',

  props: {
    // Une fiche : le type de l'objet (`Customer`) et son identifiant.
    entityType: { type: String, default: null },
    entityId: { type: String, default: null },
    // Un module, ou tous les objets d'un type dans une organisation.
    module: { type: String, default: null },
    organizationId: { type: String, default: null },
    // Un formulaire : les champs suivis, et seulement eux (`['name', 'contacts']`).
    fields: { type: Array, default: () => [] },
    // Certaines actions seulement, par le début de leur nom (`['charging-point.command-']`).
    actionPrefixes: { type: Array, default: () => [] },
    // Dans le panneau latéral : une colonne, ce qui a changé visible sans déplier.
    compact: { type: Boolean, default: false },
  },

  data() {
    return {
      items: [],
      next: null,
      loading: false,
      loadingMore: false,
      exporting: false,
      expanded: [],
      options: { modules: [], objects: [], channels: [], outcomes: [] },
      filters: { module: this.module, entityType: this.entityType, channel: null, outcome: null, from: null, to: null, includeSystem: false, operationId: null },
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('audit-trail.date'), value: 'occurredAt', width: 150 },
        { text: this.$t('audit-trail.author'), value: 'actor' },
        { text: this.$t('audit-trail.action'), value: 'action' },
        { text: this.$t('audit-trail.changes'), value: 'changes' },
        { text: this.$t('audit-trail.channel'), value: 'channel' },
        { text: '', value: 'data-table-expand' },
      ]
    },
    moduleItems() {
      return this.options.modules
    },
    objectItems() {
      return this.filters.module ? this.options.objects.filter(object => object.module === this.filters.module) : this.options.objects
    },
    channelItems() {
      return this.options.channels.map(value => ({ value, text: this.channelLabel(value) }))
    },
    outcomeItems() {
      return this.options.outcomes.map(value => ({ value, text: this.$t(`audit-trail.outcomes.${value}`) }))
    },
  },

  watch: {
    entityId() { this.load() },
    entityType(value) { this.filters.entityType = value; this.load() },
    module(value) { this.filters.module = value; this.load() },
    organizationId() { this.load() },
    fields() { this.load() },
    actionPrefixes() { this.load() },
  },

  created() {
    this.load()
    if (!this.compact && !this.entityId) {
      AuditTrailRepository.filters().then(options => { this.options = options }).catch(this.notifyError)
    }
  },

  methods: {
    params() {
      const params = {}
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value) {
          params[key] = key === 'to' ? `${value}T23:59:59` : value
        }
      })
      if (this.entityId) {
        params.entityId = this.entityId
      }
      if (this.organizationId) {
        params.organizationId = this.organizationId
      }
      if (this.fields.length) {
        params.fields = this.fields.join(',')
      }
      if (this.actionPrefixes.length) {
        params.actionPrefixes = this.actionPrefixes.join(',')
      }
      return params
    },

    async load() {
      this.loading = true
      this.expanded = []
      try {
        const page = await AuditTrailRepository.search(this.params())
        this.items = page.items
        this.next = page.next
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      this.loadingMore = true
      try {
        const page = await AuditTrailRepository.search({ ...this.params(), before: this.next })
        this.items = [...this.items, ...page.items]
        this.next = page.next
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.loadingMore = false
      }
    },

    moduleChanged() {
      if (this.filters.entityType && !this.objectItems.some(object => object.value === this.filters.entityType)) {
        this.filters.entityType = null
      }
      this.load()
    },

    showOperation(operationId) {
      this.filters.operationId = operationId
      this.load()
    },

    async exportCsv() {
      this.exporting = true
      try {
        const url = await AuditTrailRepository.export(this.params())
        const link = document.createElement('a')
        link.href = url
        link.download = 'action-history.csv'
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.exporting = false
      }
    },

    summary(item) {
      const labels = item.changes.map(change => change.label)
      return labels.length > 3 ? `${labels.slice(0, 3).join(', ')} +${labels.length - 3}` : labels.join(', ')
    },

    kindColor(kind) {
      return { created: 'success', updated: 'blue-grey', deleted: 'error', attempted: 'warning', noted: 'grey' }[kind] || 'grey'
    },

    channelLabel(channel) {
      const key = `audit-trail.channels.${channel}`
      return this.$te(key) ? this.$t(key) : channel
    },

    sourceLabel(source) {
      const key = `audit-trail.sources.${source}`
      return this.$te(key) ? this.$t(key) : source
    },

    // Les points et les tirets d'une action (« manufacturer.merged ») couperaient la clé de traduction.
    actionLabel(action) {
      const key = `audit-trail.actions.${String(action).replace(/\.\d+$/, '').replace(/\./g, '--')}`
      return this.$te(key) ? this.$t(key) : action
    },

    reasonLabel(reason) {
      return this.$te(reason) ? this.$t(reason) : reason
    },

    // Le rang dans une liste (`formulas/1/price`), pour distinguer deux éléments qui portent le même libellé.
    position(change) {
      const match = change.field.match(/\/(\d+)(-removed)?(\/|$)/)
      return match ? ` #${Number(match[1]) + 1}` : ''
    },

    // Le nom de ce que la valeur désigne quand il est connu, sinon la valeur.
    display(label, value) {
      if (label) {
        return label
      }
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      if (typeof value === 'boolean') {
        return this.$t(value ? 'yes' : 'no')
      }
      // Une date stockée à minuit UTC est une date sans heure : la convertir la décalerait.
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z$/.test(value)) {
        return value.slice(0, 10).split('-').reverse().join('/')
      }
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
        return this.$options.filters.formatDate(value, 'DD/MM/YYYY HH:mm')
      }
      return typeof value === 'object' ? JSON.stringify(value) : String(value)
    },
  },
}
</script>

<style scoped>
.audit-trail__entry {
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.audit-trail >>> th,
.audit-trail >>> td {
  text-align: left !important;
}

.audit-trail__detail {
  background: rgba(0, 0, 0, 0.02);
  padding: 12px 16px !important;
}

.audit-trail__fields {
  font-size: 0.8rem;
  border-collapse: collapse;
}

.audit-trail__fields td {
  padding: 1px 8px 1px 0;
  vertical-align: top;
  word-break: break-word;
}

.audit-trail__before {
  color: #b71c1c;
}

.audit-trail__after {
  color: #1b5e20;
}
</style>
